import type { MeasuredCellParent } from 'react-virtualized/dist/es/CellMeasurer';
import type { InfiniteLoader } from 'react-virtualized/dist/es/InfiniteLoader';

// eslint-disable-next-line
import { CellMeasurerCache, CellMeasurer, InfiniteLoader as VirtualizedInfiniteLoader, List as VirtualizedList } from 'react-virtualized';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import classNames from 'classnames';
import React from 'react';

import { ListItem } from './ListItem';

export interface ListGroupHeaderOption {
  tooltipLabelDescription?: string;
  className?: string;
  label: string;
  value: string;
}
export type ListOption<T> = {
  content?: ({
    isSelected,
    /**
     * Call on script, img, link, picture, iframe, object, source
     */
    measure,
    onClick,
    onSelected,
  }: {
    onSelected?: (
      option:
        | ({ type: 'ListHeaderOption' } & ListGroupHeaderOption)
        | ({ type?: 'ListOption' } & ListOption<T>),
    ) => void;
    option:
      | ({ type: 'ListHeaderOption' } & ListGroupHeaderOption)
      | ({ type?: 'ListOption' } & ListOption<T>);
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => Promise<unknown> | void;
    isSelected?: boolean;
    measure: () => void;
  }) => React.ReactElement<HTMLLinkElement>;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => Promise<unknown> | void;
  tooltipLabelDescription?: string;
  disabledTooltip?: string;
  destructive?: boolean;
  description?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  value: string;
  'data-testid'?: string;
} & T;

export interface ListProps<T = unknown> {
  /**
   * Callback triggered when last item is intersecting
   */
  onIntersection: ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => Promise<unknown>;
  /**
   * Items to render.
   */
  items: Array<
    | ({ type: 'ListHeaderOption' } & ListGroupHeaderOption)
    | ({ type?: 'ListOption' } & ListOption<T>)
  >;
  style?: { height: number; width: number } & React.CSSProperties;
  onSelected?: (selected: ListProps<T>['items'][number]) => void;
  /**
   * If true, selected item will not be highlighted.
   */
  withoutSelectedHighlight?: boolean;
  selected?: ListProps<T>['items'];
  tooltipLabelDescription?: string;
  children?: React.ReactNode;

  className?: string;
  /**
   * Caps the visible list height in px. When set, the height is sized to the
   * number of rows actually rendered (`rowCount` — which can include options
   * the caller injects client-side that aren't part of the server `total`) and
   * clamped to this value, so the caller controls how many rows show before it
   * scrolls. When omitted, height falls back to the legacy `total`-based sizing.
   */
  maxHeight?: number;
  /**
   * Fills its parent's height instead of sizing itself (`maxHeight` and the
   * row estimate are then ignored): for a list that IS the content of its
   * container — the phone's picker sheet — so it uses the room there is and
   * scrolls only past it. The parent must be a flex column with room to give.
   */
  fill?: boolean;
  /**
   * At non infinite lists, this is the number is same as total, no need to be provided
   */
  rowCount?: number;
  /**
   * Minimum number of items to be rendered. Defaults to 25, provide different number if list is fetching different number.
   */
  limit?: number;
  /**
   * Total number of items.
   */
  total: number;
}

export const List = React.forwardRef(
  <T,>(
    {
      className,
      fill = false,
      items,
      limit = 25,
      maxHeight,
      onIntersection,
      onSelected,
      rowCount,
      selected,
      style,
      total,
      withoutSelectedHighlight,
    }: ListProps<T>,
    ref: React.ForwardedRef<InfiniteLoader>,
  ) => {
    const cache = React.useRef(
      // In this example, average cell height is assumed to be about 56px.
      // This value will be used for the initial `Grid` layout.
      // Width is not dynamic.
      new CellMeasurerCache({
        defaultHeight: 56,
        defaultWidth: 400,
        fixedHeight: false,
        fixedWidth: true,
      }),
    );
    const rowRenderer = React.useCallback(
      ({
        index,
        key,
        parent,
        style: childStyle,
      }: {
        parent: MeasuredCellParent;
        style: React.CSSProperties;
        index: number;
        key: string;
      }) => {
        const element = items[index];

        if (!element) return null;

        return (
          <CellMeasurer
            cache={cache.current}
            rowIndex={index}
            columnIndex={0}
            parent={parent}
            key={key}
          >
            {({ measure, registerChild }) => {
              const headerElement = element as {
                type: 'ListHeaderOption';
              } & ListGroupHeaderOption;
              if (headerElement.type)
                return (
                  <ListItem
                    tooltipLabelDescription={element.tooltipLabelDescription}
                    withoutSelectedHighlight={withoutSelectedHighlight}
                    className={element?.className}
                    label={element.label ?? ''}
                    withoutHover
                  />
                );
              const option = element as ListOption<T>;
              const handleClick = async (
                e: React.MouseEvent<HTMLDivElement>,
              ) => {
                if (option.disabled) return;
                // `onSelected` is not bookkeeping — `Autocomplete.handleSelect`
                // receives it and ends in `setOpen(false)`, so this is what
                // CLOSES the menu the option was clicked in. It has to be
                // reached whether the handler is synchronous or not.
                //
                // It used to hang off `option.onClick(e)?.then(…)`, which a
                // handler returning nothing short-circuits — leaving the ⋯ menu
                // open after every plain (non-`async`) action. A rejected
                // handler still does NOT close the menu, and its rejection is
                // left to propagate, both unchanged.
                const clicked = option.onClick?.(e);
                if (clicked) {
                  clicked.then(() => {
                    onSelected?.(option);
                  });
                } else {
                  onSelected?.(option);
                }
              };
              const isSelected =
                selected?.some(
                  (selectedOption) => selectedOption.value === option.value,
                ) ?? false;
              return (
                <div ref={registerChild} style={childStyle}>
                  {option.content && typeof option.content === 'function' ? (
                    option.content({
                      isSelected,
                      measure,
                      onClick: handleClick,
                      onSelected,
                      option,
                    })
                  ) : (
                    <ListItem
                      tooltipLabelDescription={option.tooltipLabelDescription}
                      withoutSelectedHighlight={withoutSelectedHighlight}
                      disabledTooltip={option.disabledTooltip}
                      data-testid={option['data-testid']}
                      description={option.description}
                      destructive={option.destructive}
                      className={option?.className}
                      disabled={option.disabled}
                      label={option.label ?? ''}
                      isSelected={isSelected}
                      onClick={handleClick}
                    />
                  )}
                </div>
              );
            }}
          </CellMeasurer>
        );
      },
      [items, onSelected, withoutSelectedHighlight, selected],
    );

    const styles = React.useMemo(() => {
      if (fill) {
        return { ...style, flex: '1 1 0', minHeight: 0 };
      }
      const rowHeight = cache.current.defaultHeight;
      return {
        ...style,
        height: '100%',
        minHeight:
          maxHeight !== null && maxHeight !== undefined
            ? Math.min((rowCount ?? total ?? 0) * rowHeight, maxHeight)
            : Math.min((total || 0) * rowHeight, 400),
      };
    }, [fill, style, total, rowCount, maxHeight]);

    return (
      <div className={classNames(className)} style={styles}>
        <AutoSizer items={items}>
          {({ height, width }) => {
            return (
              <VirtualizedInfiniteLoader
                isRowLoaded={({ index }) => index < items.length}
                loadMoreRows={onIntersection}
                minimumBatchSize={limit}
                rowCount={total}
                ref={ref}
              >
                {({ onRowsRendered, registerChild }) => (
                  <VirtualizedList
                    deferredMeasurementCache={cache.current}
                    rowHeight={cache.current.rowHeight}
                    onRowsRendered={onRowsRendered}
                    rowCount={rowCount ?? total}
                    rowRenderer={rowRenderer}
                    height={fill ? height : (styles.minHeight as number)}
                    ref={registerChild}
                    width={width}
                  />
                )}
              </VirtualizedInfiniteLoader>
            );
          }}
        </AutoSizer>
      </div>
    );
  },
);

List.displayName = 'List';
